import {isPlatformBrowser} from '@angular/common';
import {Component, computed, effect, inject, input, PLATFORM_ID} from '@angular/core';
import {
	IonBackButton,
	IonButtons,
	IonHeader,
	IonIcon,
	IonButton,
	IonMenuButton,
	IonTitle,
	IonToolbar
} from '@ionic/angular/standalone';
import {TranslocoDirective} from '@jsverse/transloco';
import {addIcons} from 'ionicons';
import {
	atOutline,
	bagRemoveOutline,
	bookOutline,
	checkboxOutline,
	colorWandOutline,
	documentText,
	documentTextOutline,
	alertCircleOutline,
	flagOutline,
	flameOutline,
	globeOutline,
	helpBuoyOutline,
	keyOutline,
	libraryOutline,
	megaphoneOutline,
	personOutline,
	searchOutline,
	shieldOutline,
	shieldCheckmarkOutline,
	chatbubbleEllipsesOutline,
	chatboxEllipsesOutline,
	chatbubblesOutline,
	homeOutline,
	mailOutline,
	peopleOutline,
	chevronForwardOutline,
	chevronBackOutline
} from 'ionicons/icons';
import {Router, RouterLink} from '@angular/router';
import {ProfileService, SeoService, TranslateHelperService} from '@services';
import {LanguageButtonComponent} from '../language-button/language-button.component';

@Component({
	           selector: 'app-header',
	           standalone: true,
	           imports: [
			TranslocoDirective,
		           IonBackButton,
		           IonButtons,
		           IonHeader,
		           IonToolbar,
		           IonTitle,
		           IonIcon,
		           IonButton,
		           IonMenuButton,
		           LanguageButtonComponent,
		           RouterLink
	           ],
	           templateUrl: './header.component.html',
	           styleUrl: './header.component.scss'
           })
export class HeaderComponent {
	title = input.required<string>();
	icon = input.required<string>();
	translateTitle = input<boolean>(true);
	backHref = input<string | null>(null);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly isBrowser = isPlatformBrowser(this.platformId);
	protected isMobile = computed(() => (this.isBrowser ? window.innerWidth <= 600 : false));
	public readonly translateService: TranslateHelperService = inject(TranslateHelperService);
	private profileService = inject(ProfileService, {optional: true});
	private seoService: SeoService = inject(SeoService);
	private router = inject(Router);
	protected readonly isAuthenticated = computed(() => !!this.profileService?.profile()?.uuid);
	protected readonly shouldShowLoginButton = computed(() => {
		if (this.isAuthenticated()) {
			return false;
		}
		const titleValue = this.title();
		const currentUrl = this.router.url;
		// Check by title (for blog, test, landing)
		const publicTitles = ['landing', 'blog', 'test'];
		if (publicTitles.includes(titleValue)) {
			return true;
		}
		// Check by URL for bdsm-contract (title is translated)
		const publicPages = ['/blog', '/test-bdsm', '/bdsm-contract', '/landing'];
		return publicPages.some(page => currentUrl.includes(page));
	});

	protected readonly onLanguageChanged = async (nextLang: string) => {
		if (this.profileService && this.isAuthenticated()) {
			await this.profileService.switchLang(nextLang);
		}
	};

	constructor() {
		addIcons({
			globeOutline,
			personOutline,
			keyOutline,
			atOutline,
			bagRemoveOutline,
			bookOutline,
			checkboxOutline,
			flameOutline,
			documentText,
			documentTextOutline,
			alertCircleOutline,
			flagOutline,
			colorWandOutline,
			libraryOutline,
			mailOutline,
			peopleOutline,
			helpBuoyOutline,
			megaphoneOutline,
			searchOutline,
			shieldOutline,
			shieldCheckmarkOutline,
			chatbubbleEllipsesOutline,
			chatboxEllipsesOutline,
			chatbubblesOutline,
			homeOutline,
			chevronForwardOutline,
			chevronBackOutline
		});
		effect(() => {
			if (this.title()) {
				const key = this.title();
				if (this.translateTitle()) {
					const titleCandidates = [`MetaTitle.${key}`, `page.label.${key}`];
					this.seoService.update({titleKey: titleCandidates, title: undefined});
				} else {
					this.seoService.update({title: key, titleKey: undefined});
				}
			}
		});
	}
}
